import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

import { api, entityId, normalizeList, type JsonRecord } from "../lib/api.js";
import { useSession } from "../session.js";
import { CrmPhoneCallPanel } from "./CrmPhoneCallPanel.js";

export function IncomingPhoneCalls() {
  const { data: session } = useSession();
  if (!session?.features.crm) return null;
  return <IncomingCallsInner key={`${session.activeTenant.id}:${session.user.id}`} />;
}

function IncomingCallsInner() {
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const tenantId = session?.activeTenant.id ?? "";
  const [visible, setVisible] = useState(() => document.visibilityState !== "hidden");
  useEffect(() => {
    const update = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const calls = useQuery({
    queryKey: ["crm", "incoming-phone-calls", tenantId, session?.user.id],
    queryFn: ({ signal }) =>
      api.get<unknown>("/crm/phone-calls/incoming/recent", signal).then(normalizeList<JsonRecord>),
    enabled: Boolean(tenantId && session?.features.crm && visible),
    refetchInterval: 2_000,
    refetchIntervalInBackground: false,
  });

  const [tracked, setTracked] = useState<JsonRecord[]>([]);
  const [received, setReceived] = useState<JsonRecord[] | undefined>(undefined);
  if (calls.data && calls.data !== received) {
    setReceived(calls.data);
    const merged = new Map(tracked.map((call) => [entityId(call), call]));
    for (const call of calls.data) merged.set(entityId(call), call);
    setTracked([...merged.values()]);
  }
  const visibleCalls = tracked.filter((call) => !dismissed.has(entityId(call)));
  const refreshCommunications = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["crm", "organization-overview"] });
  }, [queryClient]);

  if (!visibleCalls.length) return null;
  return (
    <>
      {visibleCalls.map((call) => {
        const id = entityId(call);
        return (
          <CrmPhoneCallPanel
            key={id}
            initialCall={call}
            canEdit={session?.features.crmWrite === true}
            onClose={() => setDismissed((current) => new Set(current).add(id))}
            onCommunicationCreated={refreshCommunications}
          />
        );
      })}
    </>
  );
}
